import { createSlice } from '@reduxjs/toolkit'
import { get } from 'lodash-es'
import Status from '@constants/request_statuses.ts'
import { saveOptions } from './actions/ClientActions.js'

import { createLogger } from '@utils/logger.ts'

const log = createLogger('resourceOptions')
log.setLevel('info')

const initialState = {
  options: {
    /*
    // Example structure for a resource with type "View" and id 40
    'View:40': {
      // latest options from the server
      currentOptions: {}, 
      // options that have been changed but not saved
      stagedOptions: {},
      // original values of the options that have been changed. Allows us to determine if a change has been made
      initialChanges: {}, 
    },
    */
  },
  saveOptions: {
    // 'View:40': { status: 'idle', error: null }
  },
}

const resourceOptionsSlice = createSlice({
  name: 'resourceOptions',
  initialState,
  reducers: {
    initializeResource(state, action) {
      log.debug('initializeResource called with payload:', action.payload)
      const { resourceType, resourceId, options } = action.payload
      if (!resourceType || !resourceId || !options) {
        log.warn('initializeResource called with invalid payload:', action.payload)
        return
      }
      const resourceKey = `${resourceType}:${resourceId}`

      if (state.options[resourceKey]) {
        state.options[resourceKey].currentOptions = { ...options }
      } else {
        state.options[resourceKey] = {
          currentOptions: options,
          stagedOptions: {},
          initialChanges: {},
        }
      }
    },
    stageOptions(state, action) {
      log.debug('stageOptions called with payload:', action.payload)
      const { resourceType, resourceId, changes } = action.payload
      if (!resourceType || !resourceId || !changes) {
        log.warn('stageOptions called with invalid payload:', action.payload)
        return
      }
      const resourceKey = `${resourceType}:${resourceId}`
      if (!state.options[resourceKey]) return
      Object.keys(changes).forEach(key => {
        if (!(key in state.options[resourceKey].initialChanges)) {
          state.options[resourceKey].initialChanges[key] = state.options[resourceKey].currentOptions[key]
        }
        if (changes[key] === state.options[resourceKey].initialChanges[key]) {
          delete state.options[resourceKey].stagedOptions[key]
          return
        } else {
          state.options[resourceKey].stagedOptions[key] = changes[key]
        }
      })
    },
    resetStagedOptions(state, action) {
      log.debug('resetStagedOptions called with payload:', action.payload)
      const { resourceType, resourceId } = action.payload
      if (!resourceType || !resourceId) {
        log.warn('resetStagedOptions called with invalid payload:', action.payload)
        return
      }
      const resourceKey = `${resourceType}:${resourceId}`
      if (!state.options[resourceKey]) return

      state.options[resourceKey].stagedOptions = {}
      state.options[resourceKey].initialChanges = {}
    },
    saveOptionsComplete(state, action) {
      log.debug('saveOptionsComplete called with payload:', action.payload)
      const { resourceType, resourceId, options } = action.payload
      if (!resourceType || !resourceId || !options) {
        log.warn('saveOptionsComplete called with invalid payload:', action.payload)
        return
      }
      const resourceKey = `${resourceType}:${resourceId}`
      state.options[resourceKey] = {
        currentOptions: options,
        stagedOptions: {},
        initialChanges: {},
      }
      state.saveOptions[resourceKey] = {
        status: Status.IDLE,
        error: null,
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(saveOptions.pending, (state, action) => {
        log.debug('saveOptions.pending called with meta.arg:', action.meta.arg)
        const { resourceType, resourceId } = action.meta.arg
        if (!resourceType || !resourceId) {
          log.warn('saveOptions.pending called with invalid meta.arg:', action.meta.arg)
          return
        }
        const resourceKey = `${resourceType}:${resourceId}`
        state.saveOptions[resourceKey] = {
          status: Status.LOADING,
          error: null,
        }
      })
      .addCase(saveOptions.fulfilled, (state, action) => {
        log.debug('saveOptions.fulfilled called with meta.arg:', action.meta.arg)
        const { resourceType, resourceId } = action.meta.arg
        if (!resourceType || !resourceId) {
          log.warn('saveOptions.fulfilled called with invalid meta.arg:', action.meta.arg)
          return
        }
        const resourceKey = `${resourceType}:${resourceId}`
        state.saveOptions[resourceKey] = {
          status: Status.SUCCEEDED,
          error: null,
        }
      })
      .addCase(saveOptions.rejected, (state, action) => {
        log.debug('saveOptions.rejected called with meta.arg:', action.meta.arg)
        const { resourceType, resourceId } = action.meta.arg
        if (!resourceType || !resourceId) {
          log.warn('saveOptions.rejected called with invalid meta.arg:', action.meta.arg)
          return
        }
        const resourceKey = `${resourceType}:${resourceId}`
        state.saveOptions[resourceKey] = {
          status: Status.FAILED,
          error: action.payload,
        }
      })
  },
})

export const { initializeResource, stageOptions, resetStagedOptions, saveOptionsComplete } =
  resourceOptionsSlice.actions

export const selectBase = state => state.data.resourceOptions

export const selectCurrentOptions = (state, resourceType, resourceId) => {
  const base = selectBase(state)
  const resourceKey = `${resourceType}:${resourceId}`
  return base?.options?.[resourceKey]?.currentOptions || {}
}

export const selectStagedOptions = (state, resourceType, resourceId) => {
  const base = selectBase(state)
  const resourceKey = `${resourceType}:${resourceId}`
  return base?.options?.[resourceKey]?.stagedOptions || {}
}

export const selectInitialChanges = (state, resourceType, resourceId) => {
  const base = selectBase(state)
  const resourceKey = `${resourceType}:${resourceId}`
  return base?.options?.[resourceKey]?.initialChanges || {}
}

export const selectSaveOptionsStatus = (state, resourceType, resourceId) => {
  const resourceKey = `${resourceType}:${resourceId}`
  const returnVal = get(selectBase(state), ['saveOptions', resourceKey, 'status']) || Status.IDLE
  return returnVal
}

export const selectSaveOptionsError = (state, resourceType, resourceId) => {
  const resourceKey = `${resourceType}:${resourceId}`
  const returnVal = get(selectBase(state), ['saveOptions', resourceKey, 'error']) || null
  return returnVal
}

export default resourceOptionsSlice.reducer
